import type React from 'react';
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { HelpCircle, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useTour } from './TourContext';
import { placeCard, scrollDestination, spotlightRect } from './tourGeometry';
import type { Rect } from './tourGeometry';
import { selectionMoves } from './tourSelection';
import {
  animateWindowScroll,
  nextLayout,
  resolveTarget,
  sleep,
} from './tourDom';
import type { Cancellable } from './tourDom';

const SPRING = { type: 'spring', stiffness: 260, damping: 34 } as const;
const CARD_SPRING = { type: 'spring', stiffness: 300, damping: 30 } as const;
const DEFAULT_PADDING = 7;
const CUTOUT_RADIUS = 12;
/** Placement fallback until the card's real height is measured. */
const CARD_HEIGHT_GUESS = 210;

// Simulated-tap timing. The tour drives the report by calling the same setters
// a real tap would, which used to mean the view simply changed out from under
// you with nothing to attribute it to. Now the spotlight lands on the control
// first, a tap plays on it, and the change happens on contact — so a switch
// reads as a switch instead of a glitch.
//
// A tap is a fast gesture and has to look like one: the whole thing is ~340ms,
// about as long as a real thumb spends on a button. The unhurried part of the
// tour is the TRAVEL (see animateWindowScroll) — the taps punctuate it.
/** The finger arrives and settles before the press registers. */
const TAP_ARM_MS = 130;
/** The press itself: long enough to see the ripple leave the contact point. */
const TAP_HOLD_MS = 210;
/** After a tab switch: the incoming tab paints and its rows settle. */
const AFTER_TAB_MS = 380;
/** The roster reflows when Em Detalhe turns it interactive (the rail slides
    out) — don't measure an avatar's center until it has stopped moving. */
const ROSTER_SETTLE_MS = 200;
/** After a pick: the comparison bars re-run for the new selection. */
const AFTER_SELECT_MS = 300;
/** One beat of stillness before the card slides in. */
const PRE_CARD_MS = 170;
const TAP_DOT = 22;

interface TapPoint {
  key: number;
  x: number;
  y: number;
}

/**
 * The guided-tour overlay: a dimmed backdrop whose rounded cutout springs
 * from target to target (and rides along while the page scrolls), plus the
 * step card. Renders nothing while the tour is idle. Must be mounted inside
 * `.report-theme` (it reads the --fip-* palette) and outside the carousel
 * track (transformed ancestors would trap its fixed positioning).
 */
export const GuidedTour: React.FC = () => {
  const tour = useTour();
  const { t } = useTranslation('report');
  const reduceMotion = useReducedMotion();
  const rawId = useId();
  const maskId = `tour-mask-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  const overlayRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const prevIndexRef = useRef(0);
  const tapKeyRef = useRef(0);

  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [cutout, setCutout] = useState<Rect | null>(null);
  const [settled, setSettled] = useState(false);
  const [cardHeight, setCardHeight] = useState(0);
  const [tap, setTap] = useState<TapPoint | null>(null);

  const steps = tour?.steps;
  const stepIndex = tour?.stepIndex ?? null;
  const frame = tour?.frame ?? null;
  const skipStep = tour?.skipStep;
  const active = !!tour && stepIndex !== null;
  const step = active && steps ? steps[stepIndex] : null;

  // Step choreography: tap our way to the tab the step needs, resolve the
  // target, scroll it into place, demonstrate the selection, then let the card
  // in. Everything is cancellable — a tap mid-move just restarts the pipeline
  // on the next step.
  useEffect(() => {
    if (stepIndex === null || !frame || !steps || !skipStep) return;
    const current = steps[stepIndex];
    const dir: 1 | -1 = stepIndex >= prevIndexRef.current ? 1 : -1;
    prevIndexRef.current = stepIndex;

    let cancelled = false;
    let pending: Cancellable<unknown> | null = null;
    setSettled(false);
    setTargetEl(null);
    setTap(null);

    const run = <T,>(c: Cancellable<T>): Promise<T> => {
      pending = c;
      return c.finished;
    };
    /** A dwell beat, collapsed to nothing under reduced motion. */
    const dwell = (ms: number) =>
      reduceMotion ? Promise.resolve() : run(sleep(ms));

    const pressEl = (name: string) =>
      frame.root.querySelector<HTMLElement>(`[data-tour-press="${name}"]`);

    /** Play a tap on `el`, firing `act` at the moment of contact. With no
        element to point at (or reduced motion) the act still happens — the
        report must end up in the state the step describes either way. */
    const tapOn = async (el: HTMLElement | null, act: () => void) => {
      if (!el || reduceMotion) {
        act();
        return;
      }
      const r = el.getBoundingClientRect();
      setTap({
        key: tapKeyRef.current++,
        x: r.left + r.width / 2,
        y: r.top + r.height / 2,
      });
      await dwell(TAP_ARM_MS);
      if (cancelled) return;
      act();
      await dwell(TAP_HOLD_MS);
      if (cancelled) return;
      setTap(null);
    };

    /** Whether `el` is on screen and not hidden under the sticky header. */
    const onScreen = (el: HTMLElement) => {
      const header = frame.root.querySelector('header');
      if (header?.contains(el)) return true;
      const r = el.getBoundingClientRect();
      const top = header?.getBoundingClientRect().bottom ?? 0;
      return r.top >= top && r.bottom <= window.innerHeight;
    };

    /** Switch a tab the visible way: spotlight the control, then tap it. A
        control scrolled out of sight (a shot chip above the card being shown)
        is switched without the mime — chasing it would flick the spotlight
        to the top of the screen and back. Either way the switch renders
        before the step measures its target: a chip can add or drop a card
        above it. */
    const tapTab = async (press: string, act: () => void) => {
      const found = pressEl(press);
      const btn = found && onScreen(found) ? found : null;
      if (btn) setTargetEl(btn);
      await tapOn(btn, act);
      if (cancelled) return;
      await run(nextLayout());
      if (cancelled) return;
      if (btn) await dwell(AFTER_TAB_MS);
    };

    (async () => {
      // 1. Tabs. Guarded on the live value so a step that's already on the
      //    right tab doesn't mime a pointless tap.
      if (current.megaTab && frame.getMegaTab() !== current.megaTab) {
        await tapTab(`mega-${current.megaTab}`, () =>
          frame.setMegaTab(current.megaTab!),
        );
        if (cancelled) return;
      }
      if (current.inDepthTab && frame.getInDepthTab() !== current.inDepthTab) {
        await tapTab(`indepth-${current.inDepthTab}`, () =>
          frame.setInDepthTab(current.inDepthTab!),
        );
        if (cancelled) return;
      }
      // Roster selector mode (Players / Teams), tapped the visible way like a
      // tab. Only reached in Em Detalhe (the rail is absent otherwise), and the
      // mega-tab tap above has already brought it on screen.
      if (current.teamMode !== undefined) {
        const wantMode = current.teamMode ? 'teams' : 'players';
        if (frame.getMode() !== wantMode) {
          await tapTab(`mode-${wantMode}`, () => frame.setMode(wantMode));
          if (cancelled) return;
        }
      }
      // Shot-type chip, the same visible way. Off the Shots tab there's no
      // chip to point at, so it's set silently (the finish step's reset).
      if (current.shotType && frame.getShotType() !== current.shotType) {
        await tapTab(`chip-${current.shotType}`, () =>
          frame.setShotType(current.shotType!),
        );
        if (cancelled) return;
      }

      // 2. Park the spotlight on the step's subject.
      if (!current.target) {
        // Centered card — bring the page home and drop the cutout (a tab tap
        // may have left it sitting on a button).
        setTargetEl(null);
        await run(animateWindowScroll(0, { instant: !!reduceMotion }));
        if (cancelled) return;
      } else {
        const el = await run(resolveTarget(frame.root, current.target));
        if (cancelled) return;
        if (!el) {
          // Target never mounted (no data / single block) — drop the step in
          // the direction of travel; at the front edge always move forward.
          skipStep(stepIndex === 0 ? 1 : dir);
          return;
        }
        setTargetEl(el);
        const header = frame.root.querySelector('header');
        const destination = () => {
          const rect = el.getBoundingClientRect();
          return header && header.contains(el)
            ? 0
            : scrollDestination(
                rect.top + window.scrollY,
                rect.height,
                header?.getBoundingClientRect().bottom ?? 0,
                window.innerHeight,
                document.documentElement.scrollHeight,
              );
        };
        await run(
          animateWindowScroll(destination(), { instant: !!reduceMotion }),
        );
        if (cancelled) return;
        // The page can still move under the travel (a re-render landing late,
        // a section easing open): if the target ended up off its mark, close
        // the gap once more.
        const off = destination();
        if (Math.abs(off - window.scrollY) > 8) {
          await run(animateWindowScroll(off, { instant: !!reduceMotion }));
          if (cancelled) return;
        }
      }

      // 3. Selection. Anything the step doesn't want goes away silently (a
      //    correction, not a demonstration); the players it does want are
      //    tapped in one at a time, under the spotlight already on the roster.
      if (current.select) {
        const wanted = frame.selectionTarget(current.select);
        const { keep, adds, snapshots } = selectionMoves(
          frame.getSelection(),
          wanted,
        );
        // Applied unconditionally, even when it's already the live selection:
        // it also pins the selector back to players mode. A replay started
        // from teams mode has the avatars overlapping, and a tap aimed at one
        // would land on the pile.
        frame.setSelection(keep);
        if (adds.length && current.select === 'all') {
          // The whole roster has its own control in the header — demonstrate
          // that single tap instead of miming one per remaining avatar. The
          // setSelection above has already pinned Players, so the toggle (hidden
          // in Teams mode) is on screen by the time the roster settles.
          await dwell(ROSTER_SETTLE_MS);
          if (cancelled) return;
          await tapOn(pressEl('select-all'), () => frame.setSelection(wanted));
          if (cancelled) return;
          await dwell(AFTER_SELECT_MS);
          if (cancelled) return;
        } else if (adds.length) {
          await dwell(ROSTER_SETTLE_MS);
          if (cancelled) return;
          for (let i = 0; i < adds.length; i++) {
            const snapshot = snapshots[i];
            await tapOn(
              frame.root.querySelector<HTMLElement>(
                `[data-tour-player="${adds[i]}"]`,
              ),
              () => frame.setSelection(snapshot),
            );
            if (cancelled) return;
            await dwell(AFTER_SELECT_MS);
            if (cancelled) return;
          }
        }
      }

      await dwell(PRE_CARD_MS);
      if (cancelled) return;
      setSettled(true);
    })();

    return () => {
      cancelled = true;
      pending?.cancel();
    };
  }, [stepIndex, frame, steps, skipStep, reduceMotion]);

  // Follow the target every frame — the cutout glides with it during the
  // eased scroll and absorbs late layout shifts (entry animations, images).
  const padding = step?.padding ?? DEFAULT_PADDING;
  useEffect(() => {
    if (!targetEl) {
      setCutout(null);
      return;
    }
    let raf = 0;
    const track = () => {
      const r = targetEl.getBoundingClientRect();
      const next = spotlightRect(
        { top: r.top, left: r.left, width: r.width, height: r.height },
        padding,
      );
      setCutout((prev) =>
        prev &&
        Math.abs(prev.top - next.top) < 0.5 &&
        Math.abs(prev.left - next.left) < 0.5 &&
        Math.abs(prev.width - next.width) < 0.5 &&
        Math.abs(prev.height - next.height) < 0.5
          ? prev
          : next,
      );
      raf = requestAnimationFrame(track);
    };
    track();
    return () => cancelAnimationFrame(raf);
  }, [targetEl, padding]);

  // The page scrolls programmatically; the user's own scroll gestures are
  // parked while the tour runs. React root listeners are passive, so the
  // preventDefault has to be attached by hand.
  useEffect(() => {
    const el = overlayRef.current;
    if (!el || !active) return;
    const block = (e: Event) => e.preventDefault();
    el.addEventListener('touchmove', block, { passive: false });
    el.addEventListener('wheel', block, { passive: false });
    return () => {
      el.removeEventListener('touchmove', block);
      el.removeEventListener('wheel', block);
    };
  }, [active]);

  useEffect(() => {
    if (!active || !tour) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') tour.dismiss();
      else if (e.key === 'ArrowRight') tour.next();
      else if (e.key === 'ArrowLeft') tour.prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, tour]);

  const centered = !!step && !step.target;
  const showCard = settled && !!step && (centered || !!cutout);

  useLayoutEffect(() => {
    if (showCard && cardRef.current) {
      setCardHeight(cardRef.current.offsetHeight);
    }
  }, [showCard, stepIndex]);

  const placement = useMemo(() => {
    if (!cutout || centered) return null;
    return placeCard(
      cutout,
      cardHeight || CARD_HEIGHT_GUESS,
      window.innerHeight,
    );
  }, [cutout, centered, cardHeight]);

  if (!tour || !active || stepIndex === null || !step || !steps) return null;

  const total = steps.length;
  const isLast = stepIndex === total - 1;
  const title = t(`tour.steps.${step.id}.title` as never) as string;

  const cardWrapStyle: React.CSSProperties | undefined = centered
    ? undefined
    : placement?.position === 'bottom'
      ? { bottom: 'max(12px, env(safe-area-inset-bottom))' }
      : { top: placement?.top ?? 0 };

  return (
    <motion.div
      ref={overlayRef}
      className="fixed inset-0 z-[60]"
      style={{ touchAction: 'none' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduceMotion ? 0.12 : 0.45, ease: 'easeOut' }}
      onClick={() => tour.next()}
    >
      {/* Dim layer with the spotlight cutout punched out of it. */}
      <svg className="absolute inset-0 h-full w-full" aria-hidden="true">
        <defs>
          <mask id={maskId} maskUnits="userSpaceOnUse">
            <rect width="100%" height="100%" fill="#fff" />
            <AnimatePresence>
              {cutout ? (
                <motion.rect
                  key="cutout"
                  rx={CUTOUT_RADIUS}
                  fill="#000"
                  initial={{
                    opacity: 0,
                    x: cutout.left,
                    y: cutout.top,
                    width: cutout.width,
                    height: cutout.height,
                  }}
                  animate={{
                    opacity: 1,
                    x: cutout.left,
                    y: cutout.top,
                    width: cutout.width,
                    height: cutout.height,
                  }}
                  exit={{ opacity: 0 }}
                  transition={reduceMotion ? { duration: 0.15 } : SPRING}
                />
              ) : null}
            </AnimatePresence>
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(9, 9, 11, 0.6)"
          mask={`url(#${maskId})`}
        />
      </svg>

      {/* Hairline ring tracing the cutout. */}
      <AnimatePresence>
        {cutout ? (
          <motion.div
            key="ring"
            aria-hidden="true"
            className="pointer-events-none absolute rounded-[12px] border border-white/70"
            initial={{
              opacity: 0,
              top: cutout.top,
              left: cutout.left,
              width: cutout.width,
              height: cutout.height,
            }}
            animate={{
              opacity: 1,
              top: cutout.top,
              left: cutout.left,
              width: cutout.width,
              height: cutout.height,
            }}
            exit={{ opacity: 0 }}
            transition={reduceMotion ? { duration: 0.15 } : SPRING}
          />
        ) : null}
      </AnimatePresence>

      {/* The simulated finger: a dot that lands on the control and a ripple
          that leaves it at the moment of contact. */}
      <AnimatePresence>
        {tap ? (
          <motion.div
            key={tap.key}
            aria-hidden="true"
            className="pointer-events-none absolute"
            style={{ left: tap.x, top: tap.y }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.18 } }}
            transition={{ duration: 0.12 }}
          >
            <motion.span
              className="absolute rounded-full bg-white/85"
              style={{
                width: TAP_DOT,
                height: TAP_DOT,
                marginLeft: -TAP_DOT / 2,
                marginTop: -TAP_DOT / 2,
              }}
              initial={{ scale: 0.35 }}
              animate={{ scale: [0.35, 1, 0.8] }}
              transition={{
                duration: 0.3,
                times: [0, 0.45, 1],
                ease: 'easeOut',
              }}
            />
            <motion.span
              className="absolute rounded-full border-2 border-white"
              style={{
                width: TAP_DOT,
                height: TAP_DOT,
                marginLeft: -TAP_DOT / 2,
                marginTop: -TAP_DOT / 2,
              }}
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: [0.7, 2.4], opacity: [0.75, 0] }}
              // Leaves the contact point as the press lands (TAP_ARM_MS).
              transition={{ duration: 0.34, delay: 0.12, ease: 'easeOut' }}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* The step card. */}
      <AnimatePresence mode="wait">
        {showCard ? (
          <motion.div
            key={step.id}
            className={cn(
              'absolute inset-x-0 flex justify-center px-3',
              centered && 'inset-y-0 items-center',
            )}
            style={cardWrapStyle}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10, transition: { duration: 0.14 } }}
            transition={reduceMotion ? { duration: 0.12 } : CARD_SPRING}
          >
            <div
              ref={cardRef}
              role="dialog"
              aria-modal="true"
              aria-label={title}
              onClick={(e) => e.stopPropagation()}
              className="pointer-events-auto w-[min(404px,calc(100vw-24px))] rounded-xl border border-zinc-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium tabular-nums text-zinc-400">
                  {stepIndex + 1} / {total}
                </span>
                <button
                  type="button"
                  onClick={() => tour.dismiss()}
                  aria-label={t('tour.skip')}
                  className="-mr-1.5 -mt-1.5 flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 transition-colors active:bg-zinc-100 active:text-zinc-900"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <h2 className="mt-1.5 text-[15px] font-semibold leading-snug text-zinc-950">
                {title}
              </h2>
              {/* Bodies may name a control by drawing it: <helpIcon/> renders
                  the same lucide glyph the header button uses, so the copy
                  points at the thing itself rather than describing it. Steps
                  without a tag render as plain text. */}
              <p className="mt-1.5 text-[13px] leading-relaxed text-zinc-500">
                <Trans
                  t={t}
                  // Step ids are data, so the key can't be proven to the
                  // typed-resources union — same cast the title above uses.
                  i18nKey={`tour.steps.${step.id}.body` as never}
                  components={{
                    helpIcon: (
                      <HelpCircle
                        aria-label={t('tour.replay')}
                        className="inline-block h-[1.15em] w-[1.15em] -translate-y-[1.5px] align-middle text-zinc-600"
                      />
                    ),
                  }}
                />
              </p>

              <div className="mt-4 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => tour.prev()}
                  className={cn(
                    '-ml-2.5 h-8 rounded-md px-2.5 text-[13px] font-medium text-zinc-500 transition-colors active:bg-zinc-100 active:text-zinc-900',
                    stepIndex === 0 && 'invisible',
                  )}
                >
                  {t('tour.back')}
                </button>

                <button
                  type="button"
                  onClick={() => tour.next()}
                  className="h-8 rounded-md bg-zinc-900 px-3.5 text-[13px] font-medium text-white transition-colors active:bg-zinc-700"
                >
                  {isLast ? t('tour.done') : t('tour.next')}
                </button>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.div>
  );
};

/**
 * Mounts the overlay with an enter/exit fade. Render once per report route,
 * as a sibling of the carousel (NOT inside the embla track).
 */
export const GuidedTourLayer: React.FC = () => {
  const tour = useTour();
  return (
    <AnimatePresence>
      {tour && tour.stepIndex !== null ? (
        <GuidedTour key="guided-tour" />
      ) : null}
    </AnimatePresence>
  );
};
